import { useEffect, useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight, TrendingUp, Clock, Users, Sparkles } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useLang } from "@/contexts/LangContext";
import rezaulImg from "@/assets/doctors/rezaul-karim.jpg";
import sabbirImg from "@/assets/doctors/sabbir-ahmed.jpg";
import nusratImg from "@/assets/doctors/nusrat-jahan.jpg";
import anikaImg from "@/assets/doctors/anika-chowdhury.jpg";
import imranImg from "@/assets/doctors/imran-kabir.jpg";

type Bi = { en: string; bn: string };

type Testimonial = {
  quote: Bi;
  name: string;
  role: Bi;
  initials: string;
  photo: string;
  color: string;
  rating: number;
  outcomes: { icon: React.ComponentType<{ className?: string }>; value: Bi; label: Bi }[];
};

const TESTIMONIALS: Testimonial[] = [
  {
    quote: {
      en: "I stopped losing prescriptions. My follow-up rate doubled. My accountant actually smiles now.",
      bn: "আমি Rx হারানো বন্ধ করেছি। ফলোআপ দ্বিগুণ। অ্যাকাউন্ট্যান্ট এখন হাসেন।",
    },
    name: "Dr. Rezaul Karim",
    role: { en: "Internal Medicine · Dhanmondi", bn: "মেডিসিন · ধানমন্ডি" },
    initials: "RK",
    photo: rezaulImg,
    color: "from-[#2d8a9e] to-[#0c2340]",
    rating: 5,
    outcomes: [
      { icon: TrendingUp, value: { en: "2.1x", bn: "২.১x" }, label: { en: "follow-ups", bn: "ফলোআপ" } },
      { icon: Sparkles, value: { en: "৳86k", bn: "৳৮৬k" }, label: { en: "monthly lift", bn: "মাসিক বৃদ্ধি" } },
      { icon: Clock, value: { en: "6 min", bn: "৬ মিনিট" }, label: { en: "avg consult", bn: "গড় কনসাল্ট" } },
    ],
  },
  {
    quote: {
      en: "The AI ambient scribe saves me 90 minutes a day. I just talk to my patient — the SOAP note is ready in Bengali.",
      bn: "AI অ্যাম্বিয়েন্ট স্ক্রাইব প্রতিদিন ৯০ মিনিট বাঁচায়। শুধু কথা বলি — বাংলায় SOAP নোট রেডি।",
    },
    name: "Dr. Sabbir Ahmed",
    role: { en: "Medicine Specialist · Chattogram", bn: "মেডিসিন বিশেষজ্ঞ · চট্টগ্রাম" },
    initials: "SA",
    photo: sabbirImg,
    color: "from-sky-500 to-blue-600",
    rating: 5,
    outcomes: [
      { icon: Clock, value: { en: "90 min", bn: "৯০ মিনিট" }, label: { en: "saved daily", bn: "দৈনিক সঞ্চয়" } },
      { icon: Users, value: { en: "+18", bn: "+১৮" }, label: { en: "extra patients/wk", bn: "সাপ্তাহিক অতিরিক্ত" } },
      { icon: TrendingUp, value: { en: "38%", bn: "৩৮%" }, label: { en: "notes done same-day", bn: "একই দিনে নোট" } },
    ],
  },
  {
    quote: {
      en: "No-show rate dropped from 22% to 7% after we turned on the WhatsApp agent. It pays for itself every week.",
      bn: "WhatsApp এজেন্ট চালু করার পর নো-শো ২২% → ৭%। প্রতি সপ্তাহে খরচ উঠে আসে।",
    },
    name: "Dr. Nusrat Jahan",
    role: { en: "Pediatrics · Sylhet", bn: "শিশুরোগ · সিলেট" },
    initials: "NJ",
    photo: nusratImg,
    color: "from-emerald-500 to-teal-600",
    rating: 5,
    outcomes: [
      { icon: TrendingUp, value: { en: "22% → 7%", bn: "২২% → ৭%" }, label: { en: "no-show rate", bn: "নো-শো" } },
      { icon: Users, value: { en: "1,240", bn: "১,২৪০" }, label: { en: "WhatsApp booked", bn: "WhatsApp বুকিং" } },
      { icon: Sparkles, value: { en: "৳42k", bn: "৳৪২k" }, label: { en: "recovered/mo", bn: "মাসিক পুনরুদ্ধার" } },
    ],
  },
  {
    quote: {
      en: "Care-gap alerts brought back 40+ diabetic patients overdue for HbA1c in one month.",
      bn: "কেয়ার-গ্যাপ অ্যালার্ট এক মাসে ৪০+ HbA1c-এর অপেক্ষমাণ ডায়াবেটিক রোগী ফিরিয়ে এনেছে।",
    },
    name: "Dr. Anika Chowdhury",
    role: { en: "Endocrinology · Dhaka", bn: "এন্ডোক্রাইনোলজি · ঢাকা" },
    initials: "AC",
    photo: anikaImg,
    color: "from-fuchsia-500 to-pink-600",
    rating: 5,
    outcomes: [
      { icon: Users, value: { en: "40+", bn: "৪০+" }, label: { en: "patients returned", bn: "রোগী ফিরেছেন" } },
      { icon: TrendingUp, value: { en: "3.4x", bn: "৩.৪x" }, label: { en: "recall response", bn: "রিকল রেসপন্স" } },
      { icon: Sparkles, value: { en: "৳68k", bn: "৳৬৮k" }, label: { en: "monthly recovery", bn: "মাসিক পুনরুদ্ধার" } },
    ],
  },
  {
    quote: {
      en: "Offline prescription capture is a lifesaver during load-shedding. Nothing is ever lost.",
      bn: "লোডশেডিং-এর সময় অফলাইন Rx সত্যিই জীবনরক্ষাকারী। কিছুই হারায় না।",
    },
    name: "Dr. Imran Kabir",
    role: { en: "GP · Rajshahi", bn: "GP · রাজশাহী" },
    initials: "IK",
    photo: imranImg,
    color: "from-violet-500 to-indigo-600",
    rating: 5,
    outcomes: [
      { icon: Sparkles, value: { en: "0", bn: "০" }, label: { en: "lost prescriptions", bn: "হারানো Rx" } },
      { icon: Clock, value: { en: "100%", bn: "১০০%" }, label: { en: "sync success", bn: "সিঙ্ক সফল" } },
      { icon: Users, value: { en: "300+", bn: "৩০০+" }, label: { en: "patients/mo", bn: "মাসিক রোগী" } },
    ],
  },
];

function Stars({ n }: { n: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i < n ? "fill-amber-400 text-amber-400" : "text-gray-300"}`}
        />
      ))}
    </div>
  );
}

interface Props {
  variant?: "light" | "dark";
  className?: string;
}

export default function TestimonialsCarousel({ variant = "light", className = "" }: Props) {
  const { t, lang } = useLang();
  const displayFont =
    lang === "bn"
      ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
      : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const count = TESTIMONIALS.length;

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const isDark = variant === "dark";
  const containerBg = isDark
    ? "bg-gradient-to-br from-[#0c2340] via-[#0f2a4a] to-[#1a4a6e] text-white"
    : "bg-gradient-to-br from-white via-[#f8fafc] to-[#e6faf3] text-[#0c2340]";
  const cardBg = isDark
    ? "bg-white/5 border-white/10 backdrop-blur"
    : "bg-white border-[#e2e8f0]";
  const mutedText = isDark ? "text-white/70" : "text-[#64748b]";
  const chipBg = isDark ? "bg-white/10 border-white/20 text-white" : "bg-[#e6faf3] border-[#c7f1e2] text-[#1ba179]";

  return (
    <section
      aria-label={t("Doctor testimonials", "চিকিৎসকদের প্রশংসাপত্র")}
      className={`rounded-3xl ${containerBg} ${className} overflow-hidden`}
    >
      <div className="px-6 sm:px-10 py-12 sm:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div className="max-w-xl">
            <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.24em] border px-3 py-1.5 rounded-full mb-4 ${chipBg}`}>
              <Sparkles className="w-3.5 h-3.5" />
              {t("Real doctors, real numbers", "সত্যিকারের ডাক্তার, সত্যিকারের সংখ্যা")}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight" style={{ fontFamily: displayFont }}>
              {t(
                "Quotes that come with a receipt.",
                "প্রশংসাপত্র — সংখ্যা সহ।",
              )}
            </h2>
            <p className={`mt-2 text-[15px] leading-relaxed ${mutedText}`}>
              {t(
                "Every quote below ships with the measurable outcome that made the doctor say it.",
                "প্রতিটি প্রশংসাপত্রের সাথে সেই মাপা ফলাফল যা এই কথা বলতে বাধ্য করেছে।",
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => api?.scrollPrev()}
              aria-label={t("Previous", "পূর্ববর্তী")}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition ${
                isDark
                  ? "border-white/20 hover:bg-white/10"
                  : "border-[#cbd5e1] hover:border-[#1ba179] hover:text-[#1ba179]"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => api?.scrollNext()}
              aria-label={t("Next", "পরবর্তী")}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition ${
                isDark
                  ? "border-white/20 hover:bg-white/10"
                  : "border-[#cbd5e1] hover:border-[#1ba179] hover:text-[#1ba179]"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <Carousel
          setApi={setApi}
          opts={{ align: "start", loop: true }}
          plugins={[Autoplay({ delay: 6000, stopOnInteraction: true })]}
        >
          <CarouselContent className="-ml-4">
            {TESTIMONIALS.map((tst, i) => (
              <CarouselItem key={i} className="pl-4 md:basis-4/5 lg:basis-3/4">
                <figure className={`rounded-3xl border p-6 sm:p-8 h-full ${cardBg}`}>
                  <div className="grid lg:grid-cols-[1.4fr,1fr] gap-6 lg:gap-10">
                    <div>
                      <Quote className={`w-9 h-9 mb-4 ${isDark ? "text-[#5cbdb9]/70" : "text-[#1ba179]/50"}`} />
                      <blockquote
                        className={`text-xl sm:text-2xl leading-snug font-semibold ${isDark ? "text-white" : "text-[#0c2340]"}`}
                        style={{ fontFamily: displayFont }}
                      >
                        "{t(tst.quote.en, tst.quote.bn)}"
                      </blockquote>
                      <figcaption className="mt-6 flex items-center gap-3">
                        <img
                          src={tst.photo}
                          alt={tst.name}
                          loading="lazy"
                          width={48}
                          height={48}
                          className={`w-12 h-12 rounded-full object-cover shrink-0 ring-2 ${isDark ? "ring-white/20" : "ring-white shadow-md"}`}
                        />
                        <div className="min-w-0">
                          <div className={`font-bold text-[15px] ${isDark ? "text-white" : "text-[#0c2340]"}`}>
                            {tst.name}
                          </div>
                          <div className={`text-[12.5px] ${mutedText}`}>{t(tst.role.en, tst.role.bn)}</div>
                        </div>
                        <div className="ml-auto">
                          <Stars n={tst.rating} />
                        </div>
                      </figcaption>
                    </div>

                    <div className="grid grid-cols-3 lg:grid-cols-1 gap-3 lg:content-center">
                      {tst.outcomes.map((o, oi) => (
                        <div
                          key={oi}
                          className={`rounded-2xl border p-4 text-center lg:text-left ${
                            isDark ? "border-white/10 bg-white/5" : "border-[#e2e8f0] bg-[#f8fafc]"
                          }`}
                        >
                          <o.icon className={`w-4 h-4 mb-2 mx-auto lg:mx-0 ${isDark ? "text-[#5cbdb9]" : "text-[#1ba179]"}`} />
                          <div
                            className={`text-2xl sm:text-3xl font-extrabold ${isDark ? "text-[#5cbdb9]" : "text-[#0c2340]"}`}
                            style={{ fontFamily: displayFont }}
                          >
                            {t(o.value.en, o.value.bn)}
                          </div>
                          <div className={`text-[11px] mt-0.5 ${mutedText}`}>{t(o.label.en, o.label.bn)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </figure>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Dots */}
        <div className="mt-6 flex items-center justify-center gap-1.5">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              aria-label={`${t("Go to slide", "স্লাইড")} ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === current
                  ? isDark
                    ? "w-8 bg-[#5cbdb9]"
                    : "w-8 bg-[#1ba179]"
                  : isDark
                    ? "w-1.5 bg-white/25 hover:bg-white/40"
                    : "w-1.5 bg-[#cbd5e1] hover:bg-[#94a3b8]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
